import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { assignmentService } from '../../services/assignmentService';
import { useAuth } from '../../context/AuthContext';
import { 
  FileText, 
  Calendar, 
  Clock, 
  User, 
  BookOpen, 
  Download,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Send
} from 'lucide-react';

const AssignmentDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSubmitMode = searchParams.get('submit') === 'true';
  const isStudent = user?.role === 'student';
  
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Submission form state
  const [submissionData, setSubmissionData] = useState({
    content: '',
    files: []
  });

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentService.getAssignment(id);
      
      // The response structure is: { success, message, data: { assignment: {...} } }
      const assignmentData = response.data.data?.assignment || response.data.data;
      
      if (!assignmentData) {
        console.error('Assignment data not found in response:', response.data);
        throw new Error('Assignment data not found in response');
      }
      
      console.log('Fetched assignment data:', assignmentData);
      
      // Format assignment data to ensure all fields are present
      const formattedAssignment = {
        ...assignmentData,
        title: assignmentData.title || 'Untitled Assignment',
        description: assignmentData.description || assignmentData.instructions || 'No description provided.',
        subjectName: assignmentData.subject?.name || assignmentData.subjectName || 'N/A',
        className: assignmentData.class?.name || assignmentData.className || 'N/A',
        teacherName: assignmentData.teacher 
          ? `${assignmentData.teacher.firstName || ''} ${assignmentData.teacher.lastName || ''}`.trim() || assignmentData.teacher.email || 'N/A'
          : 'N/A',
        dueDate: assignmentData.dueDate || null
      };
      
      console.log('Formatted assignment:', formattedAssignment);
      setAssignment(formattedAssignment);
      
      // Fetch submissions based on user role
      if (isStudent) {
        // For students, fetch their own submission
        try {
          const submissionsResponse = await assignmentService.getMySubmissions({ assignmentId: id });
          const mySubmissions = submissionsResponse.data?.data?.submissions || submissionsResponse.data?.data || [];
          const existingSubmission = Array.isArray(mySubmissions) 
            ? mySubmissions.find(sub => sub.assignmentId === id || sub.assignment?.id === id)
            : null;
          
          if (existingSubmission) {
            setSubmission(existingSubmission);
            setSubmissionData({
              content: existingSubmission.submissionText || existingSubmission.content || '',
              files: existingSubmission.attachments || existingSubmission.files || []
            });
          }
        } catch (submissionError) {
          // No existing submission, that's okay
          console.log('No existing submission found');
        }
      } else {
        // For teachers/staff, fetch all submissions for this assignment
        fetchSubmissions(id);
      }
    } catch (err) {
      console.error('Assignment fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (assignmentId) => {
    try {
      setLoadingSubmissions(true);
      const response = await assignmentService.getSubmissions(assignmentId);
      // Backend returns: { success: true, data: { submissions: [...], pagination: {...} } }
      const submissionsData = response.data?.data?.submissions || [];
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAssignment();
    }
  }, [id]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setSubmissionData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index) => {
    setSubmissionData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      // Validate that content is provided
      if (!submissionData.content || !submissionData.content.trim()) {
        alert('Please enter your assignment response.');
        setSubmitting(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('content', submissionData.content.trim());
      
      submissionData.files.forEach((file, index) => {
        formData.append(`files`, file);
      });

      console.log('Submitting assignment:', {
        assignmentId: id,
        contentLength: submissionData.content.length,
        filesCount: submissionData.files.length
      });

      await assignmentService.submitAssignment(id, formData);
      
      // Refresh the assignment data
      await fetchAssignment();
      
      // Show success message
      alert('Assignment submitted successfully!');
      
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.message || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await assignmentService.downloadAssignmentFile(fileId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file');
    }
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-red-600 text-sm mb-4">{error}</div>
            <Button onClick={fetchAssignment}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (!assignment) return <div>Assignment not found</div>;

  // Safely parse due date
  let dueDate;
  let isOverdue = false;
  try {
    if (assignment.dueDate) {
      dueDate = new Date(assignment.dueDate);
      isOverdue = dueDate < new Date() && !submission;
    }
  } catch (e) {
    console.error('Error parsing due date:', e);
  }
  
  const isSubmitted = submission && (submission.status === 'submitted' || submission.status === 'graded');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{assignment.title}</h2>
            <p className="text-gray-600 mt-1">Assignment Details</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge 
            variant={isOverdue ? 'destructive' : isSubmitted ? 'default' : 'secondary'}
            className="flex items-center space-x-1"
          >
            {isOverdue ? <AlertCircle className="h-3 w-3" /> : 
             isSubmitted ? <CheckCircle className="h-3 w-3" /> : 
             <Clock className="h-3 w-3" />}
            <span className="capitalize">
              {isOverdue ? 'Overdue' : isSubmitted ? 'Submitted' : 'Pending'}
            </span>
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assignment Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Assignment Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg mb-2">{assignment.title || 'Untitled Assignment'}</h3>
                {assignment.description && assignment.description !== 'No description provided.' ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{assignment.description}</p>
                ) : (
                  <p className="text-gray-500 italic">No description provided.</p>
                )}
              </div>
              
              {assignment.instructions && (
                <div>
                  <h4 className="font-medium mb-2">Instructions:</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{assignment.instructions}</p>
                </div>
              )}

              {assignment.attachments && assignment.attachments.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Attachments:</h4>
                  <div className="space-y-2">
                    {assignment.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{file.name}</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => downloadFile(file.id, file.name)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submission Form - Only show for students */}
          {isStudent && isSubmitMode && !isSubmitted && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Send className="h-5 w-5" />
                  <span>Submit Assignment</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="content">Your Response</Label>
                  <Textarea
                    id="content"
                    placeholder="Write your assignment response here..."
                    value={submissionData.content}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, content: e.target.value }))}
                    rows={8}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="files">Upload Files</Label>
                  <Input
                    id="files"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="mt-1"
                  />
                  
                  {submissionData.files.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {submissionData.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">{file.name}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => removeFile(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={submitting || !submissionData.content.trim()}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner className="h-4 w-4 mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Assignment
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submissions List - For Teachers/Staff */}
          {!isStudent && submissions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>Submissions ({submissions.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingSubmissions ? (
                  <div className="p-6 text-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="divide-y">
                    {submissions.map((sub) => {
                      const student = sub.student || {};
                      const studentName = student.firstName && student.lastName
                        ? `${student.firstName} ${student.lastName}`
                        : student.email || 'Unknown Student';
                      
                      return (
                        <div key={sub.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <User className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">{studentName}</span>
                                <Badge variant={sub.status === 'graded' ? 'default' : 'secondary'}>
                                  {sub.status || 'submitted'}
                                </Badge>
                              </div>
                              {(sub.submissionText || sub.content) && (
                                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                  {sub.submissionText || sub.content || 'No content provided'}
                                </p>
                              )}
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                {sub.submittedAt && (
                                  <span>
                                    Submitted: {new Date(sub.submittedAt).toLocaleString()}
                                  </span>
                                )}
                                {sub.submissionDate && !sub.submittedAt && (
                                  <span>
                                    Submitted: {new Date(sub.submissionDate).toLocaleString()}
                                  </span>
                                )}
                                {(sub.score !== null && sub.score !== undefined) && (
                                  <span className="font-medium text-gray-700">
                                    Score: {sub.score}/{sub.maxScore || assignment?.points || 100}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.location.assign(`/dashboard/assignments/${id}/grade`)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Grade
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* No Submissions Message - For Teachers/Staff */}
          {!isStudent && !loadingSubmissions && submissions.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>No submissions yet</p>
              </CardContent>
            </Card>
          )}

          {/* Existing Submission - For Students */}
          {submission && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Your Submission</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Response:</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {submission.submissionText || submission.content || 'No response provided.'}
                  </p>
                </div>

                {(submission.attachments || submission.files) && (submission.attachments?.length > 0 || submission.files?.length > 0) && (
                  <div>
                    <h4 className="font-medium mb-2">Uploaded Files:</h4>
                    <div className="space-y-2">
                      {(submission.attachments || submission.files || []).map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">{file.originalname || file.filename || file.name || `File ${index + 1}`}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadFile(file.id, file.name)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {submission.grade && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded">
                    <h4 className="font-medium text-green-800 mb-2">Grade: {submission.grade}</h4>
                    {submission.feedback && (
                      <p className="text-green-700">{submission.feedback}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Assignment Info Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Assignment Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Due Date</div>
                  <div className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                    {dueDate && !isNaN(dueDate.getTime()) 
                      ? `${dueDate.toLocaleDateString()} at ${dueDate.toLocaleTimeString()}`
                      : 'Not set'}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <BookOpen className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Subject</div>
                  <div className="font-medium">{assignment.subjectName || assignment.subject?.name || 'N/A'}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Teacher</div>
                  <div className="font-medium">{assignment.teacherName || 'N/A'}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <div>
                  <div className="text-sm text-gray-500">Class</div>
                  <div className="font-medium">{assignment.className || 'N/A'}</div>
                </div>
              </div>

              {assignment.points && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Points</div>
                    <div className="font-medium">{assignment.points}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                {/* Only show Submit Assignment button for students */}
                {isStudent && !isSubmitted && !isSubmitMode && (
                  <Button 
                    className="w-full" 
                    onClick={() => window.location.assign(`/dashboard/assignments/${id}?submit=true`)}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Submit Assignment
                  </Button>
                )}
                
                {/* Show View Submissions button for teachers/staff */}
                {!isStudent && (user?.role === 'teacher' || user?.role === 'staff' || user?.role === 'admin') && (
                  <Button 
                    className="w-full" 
                    onClick={() => window.location.assign(`/dashboard/assignments/${id}/grade`)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Submissions
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Assignments
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetail;